import { useUser } from "@/hooks/useUser";
import type { PropsWithChildren } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export interface FilterContextType<Key extends string> {
  activeFilters: Set<Key>;
  toggleFilter: (filter: Key) => void;
  setAllFilters: (filters: Set<Key>) => void;
  setCustomFilterValues: (filter: Key, values: any) => void;
  customFilterValues: Map<Key, any[]>;
  reset: () => void;
}

const FilterContext = createContext<FilterContextType<any>>({
  activeFilters: new Set(),
  toggleFilter: () => {},
  setAllFilters: () => {},
  setCustomFilterValues: () => {},
  customFilterValues: new Map(),
  reset: () => {},
});

export function FilterContextProvider<Key extends string>({
  children,
  defaultFilters,
}: PropsWithChildren<{
  readonly defaultFilters: Key[];
}>) {
  const [activeFilters, setActiveFilters] = useState<Set<Key>>(
    new Set(defaultFilters),
  );
  const [customFilterValues, setCustomFilterValues] = useState<Map<Key, any[]>>(
    new Map(),
  );
  const { permissions } = useUser();

  const toggleFilter = useCallback((filter: Key) => {
    setActiveFilters((current) => {
      const newSet = new Set(current);
      if (newSet.has(filter)) {
        newSet.delete(filter);
      } else {
        newSet.add(filter);
      }
      return newSet;
    });
  }, []);

  const setAllFilters = useCallback((filters: Set<Key>) => {
    setActiveFilters(filters);
  }, []);

  const setCustomFilterValuesCallback = useCallback(
    (filter: Key, values: any) => {
      setCustomFilterValues((current) => {
        const newMap = new Map(current);
        newMap.set(filter, values);
        return newMap;
      });
    },
    [],
  );

  const reset = useCallback(() => {
    setActiveFilters(new Set(defaultFilters));
    setCustomFilterValues(new Map());
  }, [defaultFilters]);

  useEffect(() => {
    reset();
  }, [permissions, reset]);

  const value = useMemo(
    () => ({
      activeFilters,
      toggleFilter,
      setAllFilters,
      setCustomFilterValues: setCustomFilterValuesCallback,
      customFilterValues,
      reset,
    }),
    [
      activeFilters,
      toggleFilter,
      setAllFilters,
      setCustomFilterValuesCallback,
      customFilterValues,
      reset,
    ],
  );

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}

export function useFilter<Key extends string>() {
  return useContext<FilterContextType<Key>>(FilterContext);
}
